import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { LogIn, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import SEO from '../components/SEO';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { language } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);
      
      if (result.success) {
        navigate('/admin');
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-cyan-50 to-teal-50 flex items-center justify-center p-4">
      <SEO 
        title={language === 'id' ? 'Login Admin' : 'Admin Login'}
        description={language === 'id' ? 'Halaman login untuk admin Manalogi' : 'Login page for Manalogi admin'}
        noindex={true}
        nofollow={true}
      />
      {/* Background Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-200/30 rounded-full blur-3xl -z-10"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-teal-200/30 rounded-full blur-3xl -z-10"></div>

      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center space-x-2 group">
            <img
              src="/logo.png"
              alt="Manalogi Logo"
              className="h-12 w-auto object-contain transform group-hover:scale-105 transition-transform"
            />
            <span className="text-2xl font-bold text-gray-800">Manalogi</span>
          </Link>
        </div>

        <Card className="shadow-2xl border-t-4 border-t-cyan-500">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center">
              {language === 'id' ? 'Login Admin' : 'Admin Login'}
            </CardTitle>
            <CardDescription className="text-center">
              {language === 'id' 
                ? 'Masukkan kredensial Anda untuk mengakses admin panel' 
                : 'Enter your credentials to access the admin panel'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">
                  {language === 'id' ? 'Email' : 'Email'}
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@manalogi.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">
                  {language === 'id' ? 'Password' : 'Password'}
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>{language === 'id' ? 'Memuat...' : 'Loading...'}</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <LogIn className="w-4 h-4" />
                    <span>{language === 'id' ? 'Masuk' : 'Sign In'}</span>
                  </div>
                )}
              </Button>
            </form>

          </CardContent>
        </Card>

        <div className="mt-6 text-center">
          <Link to="/" className="text-sm text-gray-600 hover:text-cyan-600 transition-colors">
            {language === 'id' ? '← Kembali ke Beranda' : '← Back to Home'}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
